import { AccountLink } from '@/components/AccountLink/AccountLink'
import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { ReactEditor } from '@/components/Editor/HmqcEditor'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { ApiariesProvider } from '@/contexts/useApiaries'
import {
  ClusterRevisionReadonlyForm,
  getSystemConfigWithoutReadonlyFields,
} from '@/features/ClusterConfigs/components/ClusterRevisionForm/ClusterRevisionForm'
import { RevisionState } from '@/features/ClusterConfigs/components/States/RevisionState'
import { RevisionReviewControls } from '@/features/ClusterConfigs/pages/ClusterConfigDiff/RevisionReviewControls'
import {
  useApplyRevision,
  useApproveRevision,
  useGetClusterRevisionById,
  useGetClusterRevisionRender,
  useRejectRevision,
} from '@/generated/http-clients/v3ApiComponents'
import type { ClusterRevision } from '@/generated/http-clients/v3ApiSchemas'
import { toaster } from '@/hooks/useToast/toastUtils'
import { router } from '@/router'
import {
  Box,
  Button,
  CloseButton,
  HStack,
  Icon,
  Popover,
  Skeleton,
  Tabs,
  Tag,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import type { CustomUserClaims, UserClaims } from '@okta/okta-auth-js'
import { useOktaAuth } from '@okta/okta-react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import capitalize from 'lodash/capitalize'
import { ChevronDown, FileText, Layers, Plus } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createHandleKeyDown } from '../../utils/handleKeyDown'
import { clusterConfigDraftRoute } from '../ClusterConfigDraft/ClusterConfigDraft'
import { clusterConfigTableRoute } from '../ClusterConfigTable/ClusterConfigTable'
import { clusterConfigsRootRoute } from '../ClusterConfigsPage/ClusterConfigsPage'

export const clusterConfigDiffRoute = createRoute({
  getParentRoute: () => clusterConfigsRootRoute,
  path: '$revisionId',
  component: () => (
    <ApiariesProvider>
      <ClusterRevisionDetails />
    </ApiariesProvider>
  ),
})

export function ClusterRevisionDetails() {
  const pathParams = clusterConfigDiffRoute.useParams()
  const popoverTextareaRef = React.useRef(null)
  const approveButtonRef = React.useRef<HTMLButtonElement>(null)
  const applyButtonRef = React.useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()

  const { t } = useTranslation(['features', 'translation'], {
    useSuspense: false,
  })

  const revision = useGetClusterRevisionById({ pathParams })
  const renderedRevision = useGetClusterRevisionRender({ pathParams })

  const systemConfigWithoutReadonlyFields =
    getSystemConfigWithoutReadonlyFields(revision.data?.system)

  const revisionData = {
    ...revision.data,
    system: systemConfigWithoutReadonlyFields,
  } as ClusterRevision

  const { oktaAuth } = useOktaAuth()
  const [userData, setUserData] = React.useState<UserClaims<CustomUserClaims>>()

  React.useEffect(() => {
    let isMounted = true

    oktaAuth.getUser().then((user) => {
      if (isMounted) {
        setUserData(user)
      }
    })

    return () => {
      isMounted = false
    }
  }, [oktaAuth])

  const apiCall = {
    approve: useApproveRevision(),
    reject: useRejectRevision(),
    apply: useApplyRevision(),
  }

  const [changedState, setChangedState] = React.useState({
    comment: '',
    readyToSubmit: false,
  })

  const updateComment = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const updatedComment = event.target.value
    setChangedState((previousState) => ({
      ...previousState,
      comment: updatedComment,
      readyToSubmit: updatedComment.trim().length > 0,
    }))
  }

  const noWrapStyle = {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  }

  const stateColor = React.useMemo(() => {
    if (
      revision.data?.approval_state?.state === 'APPROVED' ||
      revision.data?.approval_state?.state === 'APPLIED'
    ) {
      return 'green'
    }
    if (revision.data?.approval_state?.state === 'REJECTED') {
      return 'red'
    }
    return 'gray'
  }, [revision])

  function performAction(action: 'reject' | 'approve' | 'apply') {
    apiCall[action]
      .mutateAsync({
        body: {
          comment: changedState.comment,
        },
        pathParams,
      })
      .then(() => {
        toaster.create({
          title: t('clusterRevisionDetails.toasts.success', {
            action: capitalize(action),
          }),
          type: 'success',
          duration: 3000,
        })
        return navigate({
          to: clusterConfigsRootRoute.to,
          params: pathParams,
        })
      })
      .catch((error: unknown) => {
        console.error('error posting', error)
        toaster.create({
          title: t('clusterRevisionDetails.toasts.error', { action }),
          type: 'error',
          duration: 5000,
        })
      })
  }

  const breadCrumbs = [
    {
      name: t('translation:sidebar.v3ClusterRevisions'),
      path: clusterConfigTableRoute.to,
    },
    {
      name: revisionData ? revisionData.hive_name : pathParams.clusterId,
      path: router.buildLocation({
        to: clusterConfigsRootRoute.to,
        params: pathParams,
      }).href,
    },
    { name: pathParams.revisionId, path: '' },
  ]

  const isSubmitted = revision.data?.approval_state?.state === 'SUBMITTED'
  const appliedBy =
    (!isSubmitted && revision.data?.rollout?.approved_by) ||
    revision.data?.rollout?.created_by

  const onApproveKeyDown = createHandleKeyDown(approveButtonRef)
  const onApplyKeyDown = createHandleKeyDown(applyButtonRef)

  return (
    <PageLayout
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      title={
        <span style={noWrapStyle}>
          {t('clusterRevisionDetails.pageTitle', {
            revisionId: pathParams.revisionId,
          })}
        </span>
      }
      description={
        <>
          {revision.isLoading && <Skeleton height="20px" />}

          {revision.isSuccess && revision.data ? (
            <HStack color="text.text-subtle">
              <Text m={0}>{t('clusterRevisionDetails.state.label')}</Text>
              <Tag.Root gap={1} colorPalette={stateColor}>
                <RevisionState approvalState={revision.data.approval_state} />
                <span>{revision.data.approval_state.state}</span>
              </Tag.Root>
              {appliedBy && (
                <>
                  <Text>{t('translation:general.by')}</Text>
                  <AccountLink account={appliedBy} />
                </>
              )}
            </HStack>
          ) : null}
        </>
      }
      action={
        <HStack gap={4}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Link
                to={clusterConfigDraftRoute.to}
                params={{
                  ...pathParams,
                  baseRevisionId: pathParams.revisionId,
                }}
              >
                <Button variant="outline">
                  <Icon as={Plus} />
                  {t('clusterRevisionDetails.actions.createNewRevision')}
                </Button>
              </Link>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>
                {t('clusterRevisionDetails.tooltips.basedOnThisRevision')}
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
          {revision.data?.approval_state?.state === 'SUBMITTED' && (
            <Popover.Root
              positioning={{ placement: 'bottom-end' }}
              initialFocusEl={() => popoverTextareaRef.current}
            >
              <RevisionReviewControls revision={revisionData} user={userData} />
              <Popover.Positioner>
                <Popover.Content>
                  <Popover.Header fontWeight="semibold" border={0}>
                    {t('clusterRevisionDetails.reviewPopover.title')}
                    <CloseButton
                      position="absolute"
                      top={2}
                      right={2}
                      color="icon.icon-base"
                      onClick={() => {
                        /* Popover will close automatically */
                      }}
                    />
                  </Popover.Header>
                  <Popover.Body>
                    <Textarea
                      placeholder={String(
                        t(
                          'clusterRevisionDetails.reviewPopover.commentPlaceholder',
                        ),
                      )}
                      value={changedState.comment}
                      onChange={updateComment}
                      onKeyDown={onApproveKeyDown}
                      ref={popoverTextareaRef}
                    />
                  </Popover.Body>
                  <Popover.Footer
                    display="flex"
                    justifyContent="end"
                    border={0}
                    gap={2}
                  >
                    <Button
                      variant="solid"
                      colorPalette="red"
                      onClick={() => performAction('reject')}
                      disabled={!changedState.readyToSubmit}
                    >
                      {t('clusterRevisionDetails.reviewPopover.reject')}
                    </Button>
                    <Button
                      variant="solid"
                      colorPalette="green"
                      onClick={() => performAction('approve')}
                      ref={approveButtonRef}
                      disabled={!changedState.readyToSubmit}
                    >
                      {t('clusterRevisionDetails.reviewPopover.approve')}
                    </Button>
                  </Popover.Footer>
                </Popover.Content>
              </Popover.Positioner>
            </Popover.Root>
          )}
          {revision.data?.approval_state?.state === 'APPROVED' && (
            <Popover.Root
              positioning={{ placement: 'bottom-end' }}
              initialFocusEl={() => popoverTextareaRef.current}
            >
              <Popover.Trigger asChild>
                <Button variant="solid">
                  {t('clusterRevisionDetails.actions.applyRevision')}
                  <Icon as={ChevronDown} mr={-1} />
                </Button>
              </Popover.Trigger>
              <Popover.Positioner>
                <Popover.Content>
                  <Popover.Header fontWeight="semibold" border={0}>
                    {t('clusterRevisionDetails.applyPopover.title')}
                    <CloseButton
                      position="absolute"
                      top={2}
                      right={2}
                      onClick={() => {
                        /* Popover will close automatically */
                      }}
                    />
                  </Popover.Header>
                  <Popover.Body>
                    <VStack alignItems="left">
                      <Text color="text.text-subtle">
                        {t(
                          'clusterRevisionDetails.applyPopover.confirmationMessage',
                        )}
                      </Text>
                      <Textarea
                        placeholder={String(
                          t(
                            'clusterRevisionDetails.applyPopover.commentPlaceholder',
                          ),
                        )}
                        value={changedState.comment}
                        onChange={updateComment}
                        onKeyDown={onApplyKeyDown}
                        ref={popoverTextareaRef}
                      />
                    </VStack>
                  </Popover.Body>
                  <Popover.Footer
                    display="flex"
                    justifyContent="end"
                    border={0}
                    gap={2}
                  >
                    <Button
                      variant="solid"
                      colorPalette="green"
                      onClick={() => performAction('apply')}
                      disabled={!changedState.readyToSubmit}
                      ref={applyButtonRef}
                    >
                      {t('clusterRevisionDetails.applyPopover.apply')}
                    </Button>
                  </Popover.Footer>
                </Popover.Content>
              </Popover.Positioner>
            </Popover.Root>
          )}
        </HStack>
      }
    >
      <Box w="100%">
        <Tabs.Root defaultValue="layers" variant="enclosed">
          <Tabs.List>
            <Tabs.Trigger value="layers">
              <Icon as={Layers} size="sm" />
              {t('clusterRevisionDetails.tabs.layers')}
            </Tabs.Trigger>
            <Tabs.Trigger value="raw">
              <Icon as={FileText} size="sm" />
              {t('clusterRevisionDetails.tabs.raw')}
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="layers">
            {revision.isLoading ? <Skeleton h="400px" /> : null}
            {(revision.isError || !revision.data) && (
              <ApiErrorState
                apiResource={revision as unknown as UseQueryResult}
                errorTitle={t('clusterRevisionDetails.errors.revisionTitle')}
                errorDescription={t(
                  'clusterRevisionDetails.errors.revisionDescription',
                )}
              />
            )}
            {revisionData && (
              <VStack gap={8}>
                <ClusterRevisionReadonlyForm data={revisionData} />
              </VStack>
            )}
          </Tabs.Content>
          <Tabs.Content value="raw">
            {renderedRevision.isLoading ? <Skeleton h="400px" /> : null}
            {renderedRevision.isError || !renderedRevision.data ? (
              <ApiErrorState
                apiResource={renderedRevision as unknown as UseQueryResult}
                errorTitle={t('clusterRevisionDetails.errors.renderTitle')}
                errorDescription={t(
                  'clusterRevisionDetails.errors.renderDescription',
                )}
              />
            ) : null}
            {renderedRevision.isSuccess && renderedRevision.data !== null && (
              <ReactEditor
                language="yaml"
                value={renderedRevision.data?.yaml}
                options={{
                  readOnly: true,
                  language: 'yaml',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  tabSize: 4,
                  insertSpaces: true,
                }}
              />
            )}
          </Tabs.Content>
        </Tabs.Root>
      </Box>
    </PageLayout>
  )
}
